import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle,
  faTag,
  faCar,
  faMapMarkerAlt,
  faCalendarAlt,
  faDollarSign,
  faShieldAlt,
  faGift,
  faCode,
  faCog,
} from '@fortawesome/free-solid-svg-icons';

const ComparisonSection = styled.section`
  ${tw`mb-12`}
`;

const SectionTitle = styled.h2`
  ${tw`text-3xl font-bold mb-4 text-text-primary`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-8 leading-relaxed text-lg`}
`;

const TableWrapper = styled.div`
  ${tw`overflow-x-auto rounded-lg shadow-card-hover`}
  background: white;
`;

const ComparisonTable = styled.table`
  ${tw`w-full border-collapse`}
  min-width: 800px;
  table-layout: fixed;
`;

const TableHeader = styled.thead``;

const TableHeaderRow = styled.tr`
  background: linear-gradient(135deg, #1e40af 0%, var(--color-black) 100%);
`;

const FeaturesHeaderCell = styled.th`
  ${tw`px-6 py-4 text-left font-bold text-white`}
  background: #1e3a8a;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  width: 60px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: rgba(255, 255, 255, 0.3);
  }
`;

const TableHeaderCell = styled.th`
  ${tw`px-4 py-4 text-center font-bold text-white text-base`}
  background: #dc2626;
  border-left: 2px solid rgba(255, 255, 255, 0.2);
  width: 15%;

  &:first-of-type {
    border-left: none;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  ${tw`border-b border-border-default transition-all duration-200`}

  &:hover {
    ${tw`bg-bg-secondary`}
    transform: scale(1.01);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FeatureCell = styled.td`
  ${tw`px-6 py-4 bg-bg-secondary border-r-2 border-border-default`}
  width: 70%;
  position: relative;
`;

const FeatureContent = styled.div`
  ${tw`flex items-center gap-3`}
`;

const FeatureIcon = styled.div`
  ${tw`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg`}
  background: linear-gradient(135deg, var(--color-black) 0%, var(--color-black) 100%);
  color: white;
  font-size: 14px;
`;

const FeatureText = styled.div`
  ${tw`flex-1`}

  .category {
    ${tw`font-bold text-blue-900 text-sm mb-1`}
  }

  .description {
    ${tw`text-text-secondary text-sm leading-relaxed`}
  }
`;

const StatusCell = styled.td`
  ${tw`px-4 py-4 text-center border-r border-border-default`}
  width: 15%;

  &:last-child {
    border-right: none;
  }
`;

const StatusIcon = styled.div<{ supported: boolean }>`
  ${tw`inline-flex items-center justify-center w-10 h-10 rounded-full`}
  ${props => (props.supported ? tw`bg-green-100 text-green-600` : tw`bg-red-100 text-red-600`)}
  font-size: 20px;
`;

const StatusText = styled.div<{ supported: boolean }>`
  ${tw`mt-2 text-sm font-medium`}
  ${props => (props.supported ? tw`text-green-700` : tw`text-red-700`)}
`;

const SpecialStatusCell = styled(StatusCell)`
  ${tw`text-center`}
  width: 15%;
`;

const SpecialStatusText = styled.div`
  ${tw`text-sm font-medium text-text-secondary`}
`;

interface ComparisonRow {
  category: string;
  feature: string;
  turo: string;
  autopilotee: string;
  icon?: any;
}

// Icon mapping for different categories
const getCategoryIcon = (category: string) => {
  const categoryLower = category.toLowerCase();
  if (categoryLower.includes('品牌') || categoryLower.includes('branding')) return faTag;
  if (categoryLower.includes('车队') || categoryLower.includes('fleet')) return faCar;
  if (categoryLower.includes('位置') || categoryLower.includes('location')) return faMapMarkerAlt;
  if (categoryLower.includes('定价') || categoryLower.includes('pricing')) return faDollarSign;
  if (categoryLower.includes('保障') || categoryLower.includes('protection')) return faShieldAlt;
  if (categoryLower.includes('营销') || categoryLower.includes('marketing')) return faGift;
  if (categoryLower.includes('高级') || categoryLower.includes('advanced')) return faCode;
  if (categoryLower.includes('私人') || categoryLower.includes('custom')) return faCog;
  return faTag;
};

interface TuroComparisonTableProps {
  language: 'zh' | 'en';
}

const TuroComparisonTable: React.FC<TuroComparisonTableProps> = ({ language }) => {
  const zhData: ComparisonRow[] = [
    {
      category: '品牌形象',
      feature: '白标 App 与网站 (使用您自己的品牌)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: '品牌形象',
      feature: '自定义域名与 Logo (https://yourbrand.com)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: '车队运营',
      feature: '换车 (行程结束前均可操作)',
      turo: '仅限开始前',
      autopilotee: '✅',
    },
    {
      category: '车队运营',
      feature: '车辆级押金设置 (不同车型设置不同押金)',
      turo: '❌',
      autopilotee: '✅',
    },
    { category: '位置功能', feature: '指定车位/位置分配', turo: '✅', autopilotee: '✅' },
    { category: '车队运营', feature: '日历管理与日期屏蔽', turo: '✅', autopilotee: '✅' },
    {
      category: '车队运营',
      feature: '最低租期设置 (可针对单车设置)',
      turo: '✅',
      autopilotee: '✅',
    },
    {
      category: '定价控制',
      feature: '自定义trip fee(由车主自定义)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: '定价控制',
      feature: '年轻人服务费控制youngster fee (由车主设置)',
      turo: '❌',
      autopilotee: '✅',
    },
    { category: '定价控制', feature: '手动加价功能 (针对单车加价)', turo: '❌', autopilotee: '✅' },
    { category: '定价控制', feature: '基于位置的定价层级', turo: '❌', autopilotee: '✅' },
    {
      category: '保障计划',
      feature: '分级保障计划 (自定义价格与级别)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: '营销推广',
      feature: '指定用户/车辆优惠券 (车辆级指定哪些车可用与用户级指定哪些用户可用)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: '营销推广',
      feature: '优惠券生效与过期日期（可随时invalid一个优惠券）',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: '营销推广',
      feature: '车辆级折扣 (长期租车/早鸟优惠， 可以定为0% off,不强制）',
      turo: '✅（强制）',
      autopilotee: '✅（不强制）',
    },
    {
      category: '高级功能',
      feature: 'API导出（可以导出api为其他网站接入）',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: '私人订制',
      feature:
        '为您随时增加您想要的功能，AI vibe coding，前Apple Staff Engineer CTO 带领团队，快速增加功能',
      turo: '❌',
      autopilotee: '✅',
    },
  ];

  const enData: ComparisonRow[] = [
    {
      category: 'Branding',
      feature: 'White-label App & Website (Use your own brand)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Branding',
      feature: 'Custom Domain & Logo (https://yourbrand.com)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Fleet Operations',
      feature: 'Vehicle Swap (Available throughout trip until completion)',
      turo: 'Only before start',
      autopilotee: '✅',
    },
    {
      category: 'Fleet Operations',
      feature: 'Vehicle-level Deposit Settings (Different deposits for different vehicle models)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Location Features',
      feature: 'Assigned Parking/Position Allocation',
      turo: '✅',
      autopilotee: '✅',
    },
    {
      category: 'Fleet Operations',
      feature: 'Calendar Management & Date Blocking',
      turo: '✅',
      autopilotee: '✅',
    },
    {
      category: 'Fleet Operations',
      feature: 'Minimum Rental Period Settings (Per-vehicle configuration)',
      turo: '✅',
      autopilotee: '✅',
    },
    {
      category: 'Pricing Control',
      feature: 'Custom Trip Fee (Set by host)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Pricing Control',
      feature: 'Youngster Fee Control (Set by host)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Pricing Control',
      feature: 'Manual Price Boost (Per-vehicle price increase)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Pricing Control',
      feature: 'Location-based Pricing Tiers',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Protection plans',
      feature: 'Tiered protection plans (custom prices and levels)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Marketing & Promotion',
      feature:
        'Targeted User/Vehicle Coupons (Vehicle-level: specify which vehicles, User-level: specify which users)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Marketing & Promotion',
      feature: 'Coupon Start & Expiration Dates (Can invalidate coupons anytime)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Marketing & Promotion',
      feature:
        'Vehicle-level Discounts (Long-term rental/Early bird discounts, can be set to 0% off, not mandatory)',
      turo: '✅ (Mandatory)',
      autopilotee: '✅ (Optional)',
    },
    {
      category: 'Advanced Features',
      feature: 'API Export (Export APIs for integration with other websites)',
      turo: '❌',
      autopilotee: '✅',
    },
    {
      category: 'Custom Development',
      feature:
        'Add any features you want anytime, AI-powered coding, led by former Apple Staff Engineer CTO, rapid feature development',
      turo: '❌',
      autopilotee: '✅',
    },
  ];

  const data = language === 'zh' ? zhData : enData;
  const title =
    language === 'zh' ? 'Autopilotee vs Turo 功能对比' : 'Autopilotee vs Turo Feature Comparison';
  const description =
    language === 'zh'
      ? 'Autopilotee 软件是专为 Turo 车主设计的白标解决方案。以下表格详细对比了 Autopilotee 软件与 Turo 平台的功能差异，帮助您了解 Autopilotee 如何为您提供更多控制权和灵活性。'
      : 'Autopilotee software is a white-label solution designed specifically for Turo hosts. The following table provides a detailed comparison between Autopilotee software and the Turo platform, helping you understand how Autopilotee offers more control and flexibility.';

  const turoHeader = language === 'zh' ? 'Turo 平台' : 'Turo Platform';
  const autopiloteeHeader = language === 'zh' ? 'Autopilotee 软件' : 'Autopilotee Software';
  const featuresLabel = language === 'zh' ? '功能' : 'Features';

  // Helper function to determine if a status is supported
  const isSupported = (status: string): boolean => {
    return status.includes('✅');
  };

  // Helper function to check if it's a special status (not just yes/no)
  const isSpecialStatus = (status: string): boolean => {
    // If it contains ✅ or ❌ but also has additional text (like "✅（强制）")
    if (status.includes('✅') && status.length > 2) {
      const cleanStatus = status.replace(/[✅❌]/g, '').trim();
      // If there's text after removing the emoji, it's special
      return cleanStatus.length > 0;
    }
    // If it doesn't contain ✅ or ❌ at all, it's special (like "仅限开始前")
    if (!status.includes('✅') && !status.includes('❌')) {
      return status.trim().length > 0;
    }
    // Pure ✅ or ❌ without additional text
    return false;
  };

  return (
    <ComparisonSection>
      <SectionTitle>{title}</SectionTitle>
      <Description>{description}</Description>
      <TableWrapper>
        <ComparisonTable>
          <TableHeader>
            <TableHeaderRow>
              <FeaturesHeaderCell>{featuresLabel}</FeaturesHeaderCell>
              <TableHeaderCell>{turoHeader}</TableHeaderCell>
              <TableHeaderCell>{autopiloteeHeader}</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => {
              const icon = getCategoryIcon(row.category);
              const turoSupported = isSupported(row.turo);
              const autopiloteeSupported = isSupported(row.autopilotee);
              const turoSpecial = isSpecialStatus(row.turo);
              const autopiloteeSpecial = isSpecialStatus(row.autopilotee);

              return (
                <TableRow key={index}>
                  <FeatureCell>
                    <FeatureContent>
                      <FeatureIcon>
                        <FontAwesomeIcon icon={icon} />
                      </FeatureIcon>
                      <FeatureText>
                        <div className="category">{row.category}</div>
                        <div className="description">{row.feature}</div>
                      </FeatureText>
                    </FeatureContent>
                  </FeatureCell>
                  {turoSpecial ? (
                    <SpecialStatusCell>
                      <SpecialStatusText>{row.turo}</SpecialStatusText>
                    </SpecialStatusCell>
                  ) : (
                    <StatusCell>
                      <StatusIcon supported={turoSupported}>
                        {turoSupported ? (
                          <FontAwesomeIcon icon={faCheckCircle} />
                        ) : (
                          <FontAwesomeIcon icon={faTimesCircle} />
                        )}
                      </StatusIcon>
                    </StatusCell>
                  )}
                  {autopiloteeSpecial ? (
                    <SpecialStatusCell>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <StatusIcon supported={autopiloteeSupported}>
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </StatusIcon>
                        <SpecialStatusText>
                          {row.autopilotee.replace(/[✅❌]/g, '').trim()}
                        </SpecialStatusText>
                      </div>
                    </SpecialStatusCell>
                  ) : (
                    <StatusCell>
                      <StatusIcon supported={autopiloteeSupported}>
                        {autopiloteeSupported ? (
                          <FontAwesomeIcon icon={faCheckCircle} />
                        ) : (
                          <FontAwesomeIcon icon={faTimesCircle} />
                        )}
                      </StatusIcon>
                    </StatusCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </ComparisonTable>
      </TableWrapper>
    </ComparisonSection>
  );
};

export default TuroComparisonTable;
